// ============================================================================
//  iOS UTILITIES - Platform detection and safe area helpers

// ============================================================================
// Provides utilities for iOS-specific optimizations in the app
//
// Usage:
//   import { isIOS, isNativeiOS, getSafeAreaInsets } from '../utils/iosUtils';
// ============================================================================

import { Capacitor } from '@capacitor/core';

// ============================================================================
// PLATFORM DETECTION
// ============================================================================

/**
 * Check if running on iOS (native or web Safari/Chrome)
 * Detects any iOS device regardless of browser or native app.
 */
export const isIOS = (): boolean => {
  return (
    Capacitor.getPlatform() === 'ios' ||
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) // iPad Pro
  );
};

/**
 * Check if running as native iOS app (Capacitor)
 */
export const isNativeiOS = (): boolean => {
  return Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform();
};

/**
 * Check if running as native app (iOS or Android)
 */
export const isNativePlatform = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if running on Android
 */
export const isAndroid = (): boolean => {
  return Capacitor.getPlatform() === 'android';
};

/**
 * Check if device has a notch (iPhone X and later)
 */
export const hasNotch = (): boolean => {
  // Check for safe area inset support
  if (typeof CSS !== 'undefined' && CSS.supports) {
    return CSS.supports('padding-top: env(safe-area-inset-top)');
  }
  return false;
};

// ============================================================================
//  iOS VOICE/VIDEO BLOCK

// ============================================================================
// Voice and video chat is not yet supported on iOS.
// This flag blocks creation of voice/video tables, purchasing voice passes,
// and joining voice/video channels on iOS devices.
// TODO: Remove this block once Agora iOS native integration is fixed.
// ============================================================================

/**
 * Voice/video is OPEN EVERYWHERE. The native iOS block is retired: the
 * Agora native plugin was verified working (mic + camera) on 2026-08-26,
 * so the app, iOS Safari/Chrome, Android, and desktop all get voice and
 * video. The function stays so every historical call site keeps working.
 * Returns true if voice/video should be BLOCKED - which is never.
 */
export const isIOSVoiceVideoBlocked = (): boolean => {
  return false;
};

// ============================================================================
// SAFE AREA HELPERS
// ============================================================================

/**
 * Get CSS safe area inset values
 * Returns fallback values if not supported
 */
export const getSafeAreaInsets = () => {
  return {
    top: 'env(safe-area-inset-top, 0px)',
    right: 'env(safe-area-inset-right, 0px)',
    bottom: 'env(safe-area-inset-bottom, 0px)',
    left: 'env(safe-area-inset-left, 0px)',
  };
};

/**
 * Get bottom padding for iOS home indicator
 * Use this for fixed bottom elements
 */
export const getBottomSafeArea = (baseValue: number = 0): string => {
  if (baseValue > 0) {
    return `calc(${baseValue}px + env(safe-area-inset-bottom, 0px))`;
  }
  return 'env(safe-area-inset-bottom, 0px)';
};

/**
 * Get top padding for iOS status bar/notch
 * Use this for fixed top elements
 */
export const getTopSafeArea = (baseValue: number = 0): string => {
  if (baseValue > 0) {
    return `calc(${baseValue}px + env(safe-area-inset-top, 0px))`;
  }
  return 'env(safe-area-inset-top, 0px)';
};

// ============================================================================
// CHAKRA UI STYLE HELPERS
// ============================================================================

/**
 * Chakra UI sx prop for iOS safe area bottom padding
 * Use on containers that need bottom padding for home indicator
 */
export const iosSafeAreaBottom = (basePadding: string | number = 0) => ({
  '@supports (padding-bottom: env(safe-area-inset-bottom))': {
    paddingBottom: basePadding 
      ? `calc(${typeof basePadding === 'number' ? basePadding + 'px' : basePadding} + env(safe-area-inset-bottom))`
      : 'env(safe-area-inset-bottom)',
  },
});

/**
 * Chakra UI sx prop for iOS safe area top padding
 * Use on containers that need top padding for notch/status bar
 */
export const iosSafeAreaTop = (basePadding: string | number = 0) => ({
  '@supports (padding-top: env(safe-area-inset-top))': {
    paddingTop: basePadding 
      ? `calc(${typeof basePadding === 'number' ? basePadding + 'px' : basePadding} + env(safe-area-inset-top))`
      : 'env(safe-area-inset-top)',
  },
});

/**
 * Combined safe area sx props for full-screen containers
 */
export const iosSafeAreaAll = {
  '@supports (padding: env(safe-area-inset-top))': {
    paddingTop: 'env(safe-area-inset-top)',
    paddingRight: 'env(safe-area-inset-right)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    paddingLeft: 'env(safe-area-inset-left)',
  },
};

// ============================================================================
// INPUT HELPERS
// ============================================================================

/**
 * iOS input styles to prevent zoom on focus
 * Font size must be 16px or larger
 */
export const iosInputStyles = {
  fontSize: '16px', // Prevents zoom on iOS
  // Disable auto-zoom
  touchAction: 'manipulation',
};

/**
 * Get input props that prevent iOS zoom
 */
export const getIOSInputProps = () => ({
  style: {
    fontSize: '16px',
    touchAction: 'manipulation',
  },
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: false,
});

// ============================================================================
// KEYBOARD HELPERS
// ============================================================================

/**
 * Check if iOS keyboard is likely visible
 * (Heuristic based on viewport height change)
 */
export const isKeyboardVisible = (): boolean => {
  if (!isIOS()) return false;
  
  // On iOS, when keyboard opens, visualViewport height decreases
  if (window.visualViewport) {
    return window.visualViewport.height < window.innerHeight * 0.75;
  }
  return false;
};

/**
 * Scroll element into view when keyboard opens
 */
export const scrollIntoViewOnFocus = (element: HTMLElement | null) => {
  if (!element || !isIOS()) return;
  
  setTimeout(() => {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 300); // Wait for keyboard animation
};

// ============================================================================
// HAPTIC FEEDBACK (Capacitor)
// ============================================================================

/**
 * Trigger haptic feedback on iOS
 * Requires @capacitor/haptics plugin
 */
export const triggerHaptic = async (style: 'light' | 'medium' | 'heavy' = 'light') => {
  if (!isNativeiOS()) return;
  
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    }[style];
    
    await Haptics.impact({ style: impactStyle });
  } catch (e) {
    // Haptics not available
  }
};

// ============================================================================
// STATUS BAR (Capacitor)
// ============================================================================

/**
 * Configure iOS status bar
 * Requires @capacitor/status-bar plugin
 */
export const configureStatusBar = async (options: {
  style?: 'dark' | 'light';
  backgroundColor?: string;
  overlay?: boolean;
}) => {
  if (!isNativeiOS()) return;
  
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    
    if (options.style) {
      await StatusBar.setStyle({ 
        style: options.style === 'dark' ? Style.Dark : Style.Light 
      });
    }
    
    if (options.backgroundColor) {
      await StatusBar.setBackgroundColor({ color: options.backgroundColor });
    }
    
    if (options.overlay !== undefined) {
      await StatusBar.setOverlaysWebView({ overlay: options.overlay });
    }
  } catch (e) {
    // Status bar not available
  }
};

export default {
  isIOS,
  isNativeiOS,
  isNativePlatform,
  isAndroid,
  hasNotch,
  getSafeAreaInsets,
  getBottomSafeArea,
  getTopSafeArea,
  iosSafeAreaBottom,
  iosSafeAreaTop,
  iosSafeAreaAll,
  iosInputStyles,
  getIOSInputProps,
  isKeyboardVisible,
  scrollIntoViewOnFocus,
  triggerHaptic,
  configureStatusBar,
};